import React, { useContext, useMemo, useState } from "react";
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import {AuthContext, IntlContext} from "context";
import { Form } from "react-final-form";
import Button from "ui/Button";
import arrayMutators from "final-form-arrays";
import { useNavigate, useParams } from "react-router";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { getErrorResponseData } from "helpers/typing";
import { BookingContext } from "../Context";
import dataFetcher from "hooks/bookService";
import InfoService from "./InfoService";
import roundNumber from "helpers/roundNumber";
import CalculOpperator from "helpers/CalculOpperator";

interface Props {
    paymentIntent: string;
}

const StripePaymentForm: React.FC<Props> = ({ paymentIntent: _paymentIntent }) => {
    const stripe = useStripe();
    const elements = useElements();
    const intl = useContext(IntlContext);
    const { account } = useContext(AuthContext);
    const [error, setError] = useState<string | undefined>();
    const { id } = useParams();
    const navigate = useNavigate();
    const { getBooking } = useContext(BookingContext);
    const bookingData = useMemo(() => {
        return getBooking();
    }, [getBooking]);
    const [isBookLoading, setIsBookLoading] = useState<boolean>(false);

    const onSubmit = async (_data: Record<string, unknown>) => {
        if (!stripe || !elements) {
            // Stripe.js n'a pas encore été chargé.
            // Assurez-vous de l'importer correctement dans votre application.
            return;
        }

        setIsBookLoading(true);

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/prestataires/${id}/reservation/confirmation`,
                },
                redirect: "if_required",
            });

            if (error) {
                if (error.type === "card_error" || error.type === "validation_error") {
                    setError(error.message);
                    setIsBookLoading(false);
                }
            } else if (paymentIntent && paymentIntent.status === "requires_capture") {
                try {
                    if (bookingData) {
                        const totalPrice = parseFloat(bookingData.price) * bookingData.nb_hours;
                        const opperator = new CalculOpperator(bookingData);
                        let [, totalAmount] = opperator.computeTTC(totalPrice);
                        let supportedCompany = 0;

                        if (account?.resources?.client && account?.resources?.client[0].company_percentage) {
                            supportedCompany =
                                totalAmount * (parseFloat(account?.resources?.client[0].company_percentage) / 100);
                        }

                        totalAmount = totalAmount - supportedCompany;

                        await dataFetcher.createConfirmPayment({
                            booking_id: bookingData?.id,
                            provider_id: bookingData?.provider_id,
                            client_id: bookingData?.client_id,
                            payment_intent: paymentIntent.id,
                            amount: roundNumber(totalAmount) * 100,
                        });

                        window.location.href = `${window.location.origin}/prestataires/${id}/reservation/confirmation`;
                    }
                } catch (err: unknown) {
                    console.error(err); // eslint-disable-line no-console
                    setIsBookLoading(false);
                }
            }
        } catch (err: unknown) {
            setIsBookLoading(false);
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={{}}
            mutators={{ ...arrayMutators }}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit} className="login__form margin--0">
                    <div className="our_services_view_container top-shadow">
                        <Button className="btn btn--secondary btn--square btn--return" onClick={() => navigate(-1)}>
                            <span className="icon icon-arrow-left"></span>
                            {intl.formatMessage("generic.action.back")}
                        </Button>
                        <div className="grid--1 grid--medium-1 grid--small-1 grid--has-gutter-3x">
                            <div className="container_form_intermediate_page">
                                <div className="container_form_intermediate_page padding-y--3">
                                    <InfoService />
                                </div>
                            </div>
                        </div>
                        <div className="grid--12 grid--medium-12 grid--small-12 grid--has-gutter-3x padding-top--3">
                            <div className="container_form_intermediate_page padding--3 grid__col--6 grid__col--small-12">
                                <h2 className="h2 margin-bottom--3">
                                    {intl.formatMessage("our_services.form.label.title")}
                                </h2>
                                <PaymentElement />
                                {error && <div>{error}</div>}
                            </div>
                        </div>
                        <div className="text-center margin-top--5">
                            <Button type="submit" className="btn btn--primary" disabled={isBookLoading}>
                                {intl.formatMessage("our_services.form.action.book_now")}
                                {isBookLoading ? <div className="loader-inner" /> : null}
                            </Button>

                            <div className="margin-top--1">
                                <i>{intl.formatMessage("our_services.form.action.book.info")}</i>
                            </div>
                        </div>
                    </div>
                </form>
            )}
        />
    );
};

export default StripePaymentForm;
